import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

// Disable browser's native scroll restoration so reloads always start at top
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

// Supabase returns auth sessions in the URL fragment (#access_token=...&refresh_token=...),
// which is data, not an anchor. Passing it to querySelector throws a SyntaxError during the
// effect commit and unmounts the whole app, so only fragments shaped like a plain element id
// are treated as scroll targets.
const isElementIdHash = (hash: string) => /^#[A-Za-z][\w-]*$/.test(hash);

export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const isAnchor = isElementIdHash(hash);

  // Run BEFORE paint to avoid flash at bottom on reload
  useLayoutEffect(() => {
    if (!isAnchor) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  useEffect(() => {
    // If there's an anchor hash, let the hash navigation handle scrolling
    if (isAnchor) {
      const scrollToHash = () => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          return true;
        }
        return false;
      };
      
      // Try immediately, then retry multiple times for lazy-loaded content
      if (!scrollToHash()) {
        const retryTimes = [100, 300, 600, 1000, 1500, 2000];
        retryTimes.forEach(delay => {
          setTimeout(scrollToHash, delay);
        });
      }
      return;
    }
    
    // Scroll to top for regular route changes without an anchor
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, [pathname, hash, isAnchor]);

  return null;
}
